import { useState } from 'react';
import { useSnackbar } from 'notistack';
import type { OAuth2LoginProps } from 'react-simple-oauth2-login';

import constants from './constants';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import config from 'modules/common/config';
import useOAuthConnection from 'modules/common/queries/oAuthConnection';

type OAuthSource = keyof typeof constants.oauthPresentation;

interface OAuthConnectOptions {
    /** Called once a connect completes, so a caller can retry what the old connection failed. */
    onConnected?: () => void;
    /** Called when the popup or the code exchange fails, so a caller can tell the member. */
    onFailed?: () => void;
}

interface OAuthCodeResponse {
    code: string;
    state: string;
    realmId?: string;
}

/**
 * Everything `OAuth2Login` needs to run a provider's connect flow: the authorize URL from `/oauth/`,
 * and the code exchange once the popup returns. `loginProps` is null while the provider has no usable
 * authorize URL, so there is nothing to offer yet.
 */
export default function useOAuthConnect(source: OAuthSource, { onConnected, onFailed }: OAuthConnectOptions = {}) {
    const [isConnecting, setIsConnecting] = useState(false);
    const [refreshFailed, setRefreshFailed] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const connection = useOAuthConnection(source);
    const { oAuthResult, createOAuthConnection, refetchoAuthResult } = connection;
    const oauth = oAuthResult?.oauth;

    // A failed refetch keeps the previous result, whose OAuth state may have expired, so the connect
    // flow is withheld until a refresh succeeds.
    const refreshOAuthState = async () => {
        const { status } = await refetchoAuthResult();
        setRefreshFailed(status === 'error');
    };

    const retryRefresh = async () => {
        setIsConnecting(true);
        try {
            await refreshOAuthState();
        } finally {
            setIsConnecting(false);
        }
    };

    const onSuccess = async (response: OAuthCodeResponse) => {
        setIsConnecting(true);
        try {
            const { warnings } = await createOAuthConnection(response.code, source, response.state, response.realmId);
            // Persisted: the reconnect dialogs close as soon as the connection lands.
            warnings.forEach((warning) => enqueueSnackbar(warning.message, { variant: 'warning', persist: true }));
            await refreshOAuthState();
            onConnected?.();
        } catch (error) {
            // Without this the rejection would go unhandled and the caller would sit unchanged
            // with no trace of why.
            sendDatadogRumError(`Failed to connect to ${source}`, error);
            onFailed?.();
            // The OAuth state expires, so a retry needs a freshly issued one.
            await refreshOAuthState();
        } finally {
            setIsConnecting(false);
        }
    };

    const onFailure = (response: unknown) => {
        sendDatadogRumError(`Failed to connect to ${source}`, new Error(`Failed to connect to ${source}`), {
            response,
        });
        setIsConnecting(false);
        onFailed?.();
    };

    const loginProps: Omit<OAuth2LoginProps, 'render'> | null =
        oauth?.clientId && oauth?.authorizationUrl && !refreshFailed
            ? {
                  authorizationUrl: oauth.authorizationUrl,
                  responseType: 'code',
                  clientId: oauth.clientId,
                  redirectUri: config.frontendURL,
                  state: oauth.state,
                  scope: oauth.scope || undefined,
                  onSuccess,
                  onFailure,
              }
            : null;

    return { ...connection, loginProps, isConnecting, refreshFailed, retryRefresh };
}
